import React, { useEffect, useState } from 'react';
import { Controller, FieldError, UseControllerProps } from 'react-hook-form';
import { Button, FormControl, FormHelperText, FormLabel, IconButton, Box, Typography, Grid, Tooltip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import config from '../../Config/config.json'

interface WSFileUploadProps extends UseControllerProps {
    label: string;
    id: string;
    name: string;
    className?: string;
    variant?: 'standard' | 'filled' | 'outlined';
    disabled?: boolean;
    acceptFileTypes?: string;
    maxFileSizeMB?: number;
    multiple?: boolean;
    image?: boolean;
    onChange?: (files: File[] | null) => void;
    align?: "Normal" | "Start"
    imgUrl?: any
    isToolTipImg?: boolean,
    defaultValue?: string | string[] | IDefaultValue | IDefaultValue[] | null // CHANGED: Support multiple types
    onRemove?: (id: any) => void,
    maxFileSize?: number
}

interface IDefaultValue {
    img: string,
    name: string,
    id?: string
}

const WSFileUpload: React.FC<WSFileUploadProps> = ({
    id,
    label,
    className,
    control,
    name,
    rules,
    variant = 'outlined',
    disabled = false,
    acceptFileTypes = ".jpg,.bmp,.pdf",
    maxFileSizeMB = 1,
    multiple = false,
    image = false,
    onChange,
    align = "Normal",
    imgUrl = undefined,
    isToolTipImg = false,
    defaultValue,
    onRemove
}) => {
    const [previews, setPreviews] = useState<string[]>([]);
    const [existingFiles, setExistingFiles] = useState<any[]>([]);
    const [error, setError] = useState<any | null>(null);

    // Clean up object URLs on unmount
    useEffect(() => {
        return () => {
            previews.forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, field: any, formState: any) => {
        const newFiles = event.target.files ? Array.from(event.target.files) : [];

        if (newFiles.length === 0) {
            return;
        }

        const invalidFiles = newFiles.filter(file => file.size > (maxFileSizeMB * 1024 * 1024));
        if (invalidFiles.length > 0) {
            formState.errors[name] = {
                type: 'validate',
                message: `Each file should be less than ${maxFileSizeMB}MB`
            } as FieldError;
            setError(formState);
            return;
        }

        // Clear existing files when new files are selected (for single upload)
        if (!multiple) {
            setExistingFiles([]);
        }

        formState.errors[name] = null;
        setError(formState);

        const updatedFiles = multiple ? [...(field.value || []), ...newFiles] : newFiles;
        field.onChange(updatedFiles);

        if (onChange) {
            onChange(updatedFiles);
        }

        if (image) {
            // Revoke old URLs
            previews.forEach(url => URL.revokeObjectURL(url));

            const imagePreviews = updatedFiles.map(file => {
                if (file.type.startsWith('image/')) {
                    return URL.createObjectURL(file);
                }
                return '';
            });
            setPreviews(imagePreviews);
        }

        // Clear the input
        event.target.value = '';
    };

    const handleRemoveFile = (index: number, field: any) => {
        const updatedFiles = field.value.filter((_: File, i: number) => i !== index);
        field.onChange(updatedFiles);

        if (image) {
            // Revoke the URL
            if (previews[index]) {
                URL.revokeObjectURL(previews[index]);
            }
            const updatedPreviews = previews.filter((_, i) => i !== index);
            setPreviews(updatedPreviews);
        }
    };

    const handleRemoveExistingFile = (index: number) => {
        const updatedFiles = existingFiles.filter((_, i) => i !== index);
        setExistingFiles(updatedFiles);

        if (onRemove) {
            onRemove(existingFiles[index]?.id);
        }
    };

    // Handle defaultValue changes
    // Handle defaultValue changes
    useEffect(() => {

        if (!defaultValue) {
            setExistingFiles([]);
            return;
        }

        const imgurlString = config.imgUrl;
        const fileObjArray: any[] = [];

        try {
            // Handle different types of defaultValue
            if (Array.isArray(defaultValue)) {
                // Case: Array of strings or IDefaultValue objects
                defaultValue?.forEach((item: any) => {
                    if (typeof item === 'string') {
                        // Array of strings
                        const imageUrl = `${imgurlString}${item}`;
                        fileObjArray.push({
                            img: imageUrl,
                            name: item,
                            id: `default-${Date.now()}-${Math.random()}`
                        });
                    } else if (typeof item === 'object' && item.img) {
                        // Array of IDefaultValue objects
                        const imageUrl = `${imgurlString}${item.img}`;
                        fileObjArray.push({
                            img: imageUrl,
                            name: item.name || item.img,
                            id: item.id || `default-${Date.now()}-${Math.random()}`
                        });
                    }
                });
            } else if (typeof defaultValue === 'string') {
                // Case: Single string
                const imageUrl = `${imgurlString}${defaultValue}`;
                fileObjArray.push({
                    img: imageUrl,
                    name: defaultValue,
                    id: 'default-single'
                });
            } else if (typeof defaultValue === 'object' && defaultValue.img) {
                // Case: Single IDefaultValue object
                const imageUrl = `${imgurlString}${defaultValue.img}`;
                fileObjArray.push({
                    img: imageUrl,
                    name: defaultValue.name || defaultValue.img,
                    id: defaultValue.id || 'default-object'
                });
            }

            setExistingFiles(fileObjArray);
        } catch (error) {
            console.error('Error processing defaultValue:', error);
            setExistingFiles([]);
        }
    }, [defaultValue]);

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState, formState }) => (
                <FormControl className={className} disabled={disabled} error={!!fieldState.error} fullWidth>
                    <FormLabel sx={{ fontSize: '0.875rem', color: 'black' }}>{label}</FormLabel>

                    <Grid item container spacing={2}>
                        <Grid item sm={12}>
                            <input
                                accept={acceptFileTypes}
                                id={id}
                                type="file"
                                style={{ display: 'none' }}
                                multiple={multiple}
                                onChange={(event: any) => handleFileChange(event, field, formState)}
                            />
                        </Grid>
                        <Grid item sm={3} style={{ marginTop: '10px', marginBottom: 10 }}>
                            <label htmlFor={id}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component="span"
                                    startIcon={<CloudUploadIcon />}
                                >
                                    {multiple ? 'Upload Files' : 'Upload File'}
                                </Button>
                            </label>
                        </Grid>
                    </Grid>

                    {/* Show newly uploaded files */}
                    {Array.isArray(field.value) && field.value.length > 0 && (
                        <Box mt={2} sx={{ maxHeight: '30rem', overflow: 'auto', width: '100%' }}>
                            {field.value.map((file: File, index: number) => (
                                <Box key={`new-${index}`} display="flex" alignItems="center" sx={{ marginTop: 2 }}>
                                    {image && previews[index] && (
                                        isToolTipImg ? (
                                            <Tooltip title={
                                                <img
                                                    src={previews[index]}
                                                    alt="preview"
                                                    style={{ width: '90px', height: '90px' }}
                                                />
                                            }>
                                                <img
                                                    src={previews[index]}
                                                    alt="preview"
                                                    style={{ width: '40px', height: '40px', marginRight: '8px' }}
                                                />
                                            </Tooltip>
                                        ) : (
                                            <img
                                                src={previews[index]}
                                                alt="preview"
                                                style={{ width: '40px', height: '40px', marginRight: '8px' }}
                                            />
                                        )
                                    )}
                                    <Typography variant="body1" style={{ marginRight: '8px' }}>
                                        {file.name}
                                    </Typography>
                                    <IconButton color="secondary" onClick={() => handleRemoveFile(index, field)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Show existing files from defaultValue */}
                    {existingFiles.length > 0 && (
                        <Box mt={2} sx={{ maxHeight: '30rem', overflow: 'auto', width: '100%' }}>
                            {existingFiles.map((item: any, index: number) => (
                                <Box key={`existing-${index}`} display="flex" alignItems="center" sx={{ marginTop: 2 }}>
                                    <img
                                        src={item.img}
                                        alt="existing"
                                        style={{ width: '40px', height: '40px', marginRight: '8px' }}
                                    />
                                    <Typography variant="body1" style={{ marginRight: '8px' }}>
                                        {item.name}
                                    </Typography>
                                    <IconButton color="secondary" onClick={() => handleRemoveExistingFile(index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
            )}
        />
    );
};

export default WSFileUpload;
